import type { ErrorInfo } from 'react'

import { captureReactException } from '@sentry/react'

import type { LoggerService } from '../LoggerService'

export function mountErrorInterceptor(logger: LoggerService): () => void {
  const onError = (event: Event) => {
    if (event instanceof ErrorEvent) {
      logger.exception(
        'js.unhandled_error',
        event.error ?? event.message,
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        },
        { category: 'runtime' }
      )
      return
    }

    const target = event.target
    const resourceUrl =
      target instanceof HTMLScriptElement || target instanceof HTMLImageElement
        ? target.src
        : target instanceof HTMLLinkElement
          ? target.href
          : undefined
    logger.error('js.resource_load_failed', {
      tagName:
        target instanceof Element ? target.tagName.toLowerCase() : undefined,
      url: resourceUrl
    })
  }

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    logger.exception(
      'js.unhandled_rejection',
      event.reason,
      {},
      { category: 'runtime' }
    )
  }

  const onSecurityPolicyViolation = (event: SecurityPolicyViolationEvent) => {
    logger.error('js.security_policy_violation', {
      effectiveDirective: event.effectiveDirective,
      blockedURI: event.blockedURI,
      sourceFile: event.sourceFile,
      lineNumber: event.lineNumber,
      columnNumber: event.columnNumber,
      disposition: event.disposition
    })
  }

  const onMessageError = (event: MessageEvent) => {
    logger.error('js.message_deserialization_failed', {
      origin: event.origin
    })
  }

  window.addEventListener('error', onError, true)
  window.addEventListener('unhandledrejection', onUnhandledRejection)
  window.addEventListener('securitypolicyviolation', onSecurityPolicyViolation)
  window.addEventListener('messageerror', onMessageError)

  return () => {
    window.removeEventListener('error', onError, true)
    window.removeEventListener('unhandledrejection', onUnhandledRejection)
    window.removeEventListener(
      'securitypolicyviolation',
      onSecurityPolicyViolation
    )
    window.removeEventListener('messageerror', onMessageError)
  }
}

export function captureReactError(
  logger: LoggerService,
  error: Error,
  info: ErrorInfo
): void {
  captureReactException(error, info)
  logger.exception(
    'react.error_boundary',
    error,
    { componentStack: info.componentStack },
    { category: 'runtime' }
  )
}
